import React from 'react';
import { Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { getImageUrl } from '../config/api';

interface ProductCardProps {
  product: Product;
  isAccessory?: boolean;
  type?: 'new' | 'refill';
  size?: '6kg' | '13kg';
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, isAccessory, type, size }) => {
  const navigate = useNavigate();

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`h-4 w-4 ${
          index < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const getBrandImage = (brand: string, size?: '6kg' | '13kg') => {
    const brandLower = brand.toLowerCase();
    
    if (brandLower === 'k-gas') {
      if (size === '6kg') {
        return getImageUrl('kgas6kg.png');
      } else if (size === '13kg') {
        return getImageUrl('kgas13kg.png');
      }
      return getImageUrl('kgas.png');
    }
    
    if (brandLower === 'total gas') {
      if (size === '6kg') {
        return getImageUrl('total6kg.png');
      } else if (size === '13kg') {
        return getImageUrl('total13kg.png');
      }
      return getImageUrl('total.png');
    }

    if (brandLower === 'supa gas') {
      if (size === '6kg') {
        return getImageUrl('supagas6kg.jpg');
      } else if (size === '13kg') {
        return getImageUrl('supagas13kg.jpg');
      }
      return getImageUrl('supagas.jpg');
    }
    
    return getImageUrl(product.image);
  };

  if (isAccessory) {
    return (
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
        onClick={() => navigate(`/product/${product.id}`)}
      >
        <img 
          src={getImageUrl(product.image)} 
          alt={product.brand}
          className="w-full h-48 object-cover"
        />
        <div className="p-4">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">{product.brand}</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{product.description}</p>
          <div className="mt-2">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              KSH {product.price}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!product.prices || !type || !size) {
    return null;
  }

  const priceInfo = {
    new: {
      title: 'New Gas and Cylinder',
      description: 'Purchase a new gas cylinder',
      priceLabel: 'Price'
    },
    refill: {
      title: 'Gas Refill',
      description: 'Refill your existing cylinder',
      priceLabel: 'Refill Price'
    }
  };

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
      onClick={() => navigate(`/product/${product.id}`)}
    >
      <img 
        src={getBrandImage(product.brand, size)} 
        alt={product.brand}
        className="w-full h-48 object-cover"
      />
      <div className="p-4">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
          {product.brand} - {size} {priceInfo[type].title}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {priceInfo[type].description}
        </p>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-1">
            {renderStars(product.rating || 4)}
            <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
              ({product.reviews || 0} reviews)
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">{priceInfo[type].priceLabel}</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              KSH {product.prices[size][type]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};